import type { Coord, WalkingLeg } from './debug-walk';
import { walkingLegs } from './debug-walk';
import { haversineM } from './haversine';
import type { NearbyVertex } from './nearby-types';
import type { JourneyJson } from '../types/JourneyJson';

/** Colour of the traced polyline — matches the project's `--color-warn` amber. */
export const TRACE_COLOR = '#d29922';

/** Fill colour for the small circle rendered at each trace vertex. */
export const VERTEX_FILL_COLOR = '#ffffff';

/** Colour of the small circle rendered at each vertex of the returned journey's walk. */
export const WALK_VERTEX_COLOR = '#58a6ff';

/** Radius (pixels) of a walk-vertex marker. */
export const WALK_VERTEX_RADIUS = 3;

/** Green fill for the origin pin — matches the project's `--color-accent-green`. */
export const ORIGIN_PIN_COLOR = '#3fb950';

/** Red fill for the destination pin — matches the project's `--color-accent-red`. */
export const DESTINATION_PIN_COLOR = '#f85149';

/** Radius (pixels) of an endpoint pin marker — bigger than trace/walk vertices so it stands out. */
export const PIN_RADIUS = 8;

/** Dash pattern for the traced polyline (Leaflet `dashArray`). */
export const TRACE_DASH = '6,3';

/** Stroke weight for the traced polyline. */
export const TRACE_WEIGHT = 3;

/** Radius (pixels) of the circle marker rendered at each trace vertex. */
export const VERTEX_MARKER_RADIUS = 4;

/** Radius (pixels) of the circle marker rendered at each overlaid graph vertex. */
export const GRAPH_MARKER_RADIUS = 3;

/** Preloaded centre coord as a numeric tuple. Alexanderplatz — mirrors the graph inspector. */
export const DEFAULT_CENTRE: readonly [number, number] = [52.522, 13.413];

/** Preloaded centre coord as `"lat,lon"` text, kept in sync with `DEFAULT_CENTRE`. */
export const DEFAULT_CENTRE_TEXT = '52.52200,13.41300';

/** Default map zoom for the pen page — close enough to lay vertices on individual sidewalks. */
export const DEFAULT_ZOOM = 18;

/** Default snap radius in metres when snap-to-graph is enabled. */
export const DEFAULT_SNAP_RADIUS_M = 15;

/** Default overlay-fetch radius in metres. */
export const DEFAULT_GRAPH_RADIUS_M = 300;

/** Lowest zoom `parseZoom` accepts. */
export const MIN_ZOOM = 1;

/** Highest zoom `parseZoom` accepts — OSM tiles top out at 19; give one step of headroom. */
export const MAX_ZOOM = 22;

/**
 * Returns the nearest vertex coord within `radiusMetres` of `coord`, or `coord`
 * unchanged when the vertex list is empty, the nearest is out of range, or the
 * radius is zero (snap disabled).
 */
export function snapToNearest(
  coord: Coord,
  vertices: readonly NearbyVertex[],
  radiusMetres: number,
): Coord {
  if (radiusMetres <= 0) return coord;
  const nearest = nearestVertex(coord, vertices);
  if (!nearest) return coord;
  return nearest.distance <= radiusMetres ? nearest.vertex.coord : coord;
}

/** Pair of a vertex plus the haversine distance in metres from a probe coord. */
interface RankedVertex {
  vertex: NearbyVertex;
  distance: number;
}

/** Nearest vertex + its distance, or `undefined` when the list is empty. */
function nearestVertex(coord: Coord, vertices: readonly NearbyVertex[]): RankedVertex | undefined {
  let best: RankedVertex | undefined;
  for (const vertex of vertices) {
    const distance = haversineM(coord, vertex.coord);
    if (!best || distance < best.distance) best = { vertex, distance };
  }
  return best;
}

/** Pretty-printed JSON for a trace: two-space indent, one element per line. */
export function traceToJson(trace: readonly Coord[]): string {
  return JSON.stringify(trace, null, 2);
}

/** Parses `text` as an integer zoom in `[MIN_ZOOM, MAX_ZOOM]`, or `undefined`. */
export function parseZoom(text: string): number | undefined {
  const parsed = Number(text.trim());
  if (!Number.isSafeInteger(parsed)) return undefined;
  if (parsed < MIN_ZOOM || parsed > MAX_ZOOM) return undefined;
  return parsed;
}

/** Parses `text` as a non-negative integer metres value; returns 0 (snap off) otherwise. */
export function parseSnapRadius(text: string): number {
  const parsed = Number(text.trim());
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.floor(parsed);
}

/** Whether the pen page has a journey rendered, has searched with no result, or hasn't searched. */
export type JourneyStatus = 'none' | 'loaded' | 'empty';

/** Sidebar text describing whether a journey is currently rendered. */
export function formatJourneyLine(status: JourneyStatus): string {
  if (status === 'loaded') return 'Journey shown on map.';
  if (status === 'empty') return 'No journey found for this pair.';
  return 'No journey loaded.';
}

/**
 * Flattens a journey's walking-leg geometries into a single `Coord[]` in the
 * same shape the trace uses, so Copy walk JSON pastes into the same tools.
 * Returns `[]` when `journey` is undefined or has no walking legs.
 */
export function journeyWalkGeometry(journey: JourneyJson | undefined): Coord[] {
  const walks = walkingLegs(journey);
  return walks.flatMap((walk: WalkingLeg) => walk.geometry.map((coord) => cloneCoord(coord)));
}

/** Returns a fresh `Coord` tuple from a wire `[lat, lon]` pair — strips readonly. */
function cloneCoord(coord: readonly [number, number]): Coord {
  return [coord[0], coord[1]];
}
